import { CurrencyPipe } from '@angular/common';
import {
  Component,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Button } from 'primeng/button';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { Subscription } from 'rxjs';
import {
  Consumable,
  consumableQuantityLabel,
  consumableUnitLabel,
} from '../../../../models/consumable.model';
import { QuoteRevisionItem } from '../../../../models/quote-revision-item.model';
import { ConsumableService } from '../../../consumable.service';
import { QuoteRevisionItemService } from '../../../services/quote-revision-item.service';

interface ConsumableDraft {
  consumable: Consumable;
  quantity: number;
}

@Component({
  selector: 'app-quote-revision-item-consumables-information',
  imports: [
    Button,
    CurrencyPipe,
    FormsModule,
    IconFieldModule,
    InputIconModule,
    InputNumberModule,
    InputTextModule,
  ],
  templateUrl: './consumables-information.component.html',
})
export class QuoteRevisionItemConsumablesInformationComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input({ required: true }) item!: QuoteRevisionItem;
  @Input({ required: true }) currencyCode!: string;
  @Input() readOnly = false;

  private readonly revisionItemService = inject(QuoteRevisionItemService);
  private readonly consumableService = inject(ConsumableService);
  private subscription?: Subscription;

  readonly unitLabel = consumableUnitLabel;
  readonly quantityLabel = consumableQuantityLabel;

  catalog: Consumable[] = [];
  searchQuery = '';
  editMode = false;
  saving = false;
  draft: ConsumableDraft[] = [];

  ngOnInit(): void {
    this.subscription = this.consumableService.activeConsumables$.subscribe(
      (rows) => (this.catalog = rows),
    );
    void this.consumableService.loadConsumables();
    this.resetDraft();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['item'] && !this.editMode) this.resetDraft();
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  get searchResults(): Consumable[] {
    const selectedIds = new Set(this.draft.map((row) => row.consumable.id));
    const needle = this.searchQuery.trim().toLowerCase();
    return this.catalog.filter(
      (row) =>
        !selectedIds.has(row.id) &&
        `${row.name} ${row.description}`.toLowerCase().includes(needle),
    );
  }

  extendedCost(cost: string, quantity: string | number): number {
    return Number(cost) * Number(quantity);
  }

  resetDraft(): void {
    this.draft = (this.item?.consumable_links ?? []).map((link) => ({
      consumable: link.consumable,
      quantity: Number(link.quantity),
    }));
    this.searchQuery = '';
  }

  startEdit(): void {
    this.resetDraft();
    this.editMode = true;
  }

  cancelEdit(): void {
    this.resetDraft();
    this.editMode = false;
  }

  addConsumable(consumable: Consumable): void {
    this.draft = [...this.draft, { consumable, quantity: 1 }];
  }

  removeConsumable(consumableId: number): void {
    this.draft = this.draft.filter((row) => row.consumable.id !== consumableId);
  }

  async save(): Promise<void> {
    if (this.readOnly) return;
    this.saving = true;
    try {
      await this.revisionItemService.updateItem(this.item.persistent_id, {
        consumable_links: this.draft.map((row) => ({
          consumable_id: row.consumable.id,
          quantity: row.quantity.toString(),
        })),
      });
      this.editMode = false;
    } finally {
      this.saving = false;
    }
  }
}
